/**
 * StepUpReasonDialog — collects the operator's audit reason for
 * destructive sysadmin mutations that require the `X-Step-Up-Reason`
 * header (#1131 / #1136).
 *
 * Per `packages/api/src/plugins/require-fresh-totp.ts` every Tier-1 +
 * Tier-2 destructive mutation requires:
 *   1. A fresh sysadmin token (the login flow already required TOTP).
 *   2. An `X-Step-Up-Reason` header of 3-500 chars describing the
 *      reason for the operation.
 *
 * Use this dialog for destructive flows that DON'T already wrap a
 * `TypedConfirmDialog` (which has its own `requireReason` field).
 * Typical use case: JWT key generate, KMS key rotate — both have
 * their own custom dialogs and need a step-up reason in addition to
 * the body params.
 *
 * Usage:
 *
 *   const [reasonOpen, setReasonOpen] = useState(false);
 *
 *   const handleSubmit = (reason: string) => {
 *     mutation.mutate({
 *       input: { organizationId, algorithm: 'RS256' },
 *       stepUpReason: reason,
 *     });
 *     setReasonOpen(false);
 *   };
 *
 *   <StepUpReasonDialog
 *     open={reasonOpen}
 *     onOpenChange={setReasonOpen}
 *     title="Audit reason for JWT key rotation"
 *     description="Tier-1 step-up: this rotation drains the prior active key over 7 days. The reason is recorded in the platform_audit_logs row."
 *     onConfirm={handleSubmit}
 *     submitting={mutation.isPending}
 *   />
 */

import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { Label } from './label';
import { Textarea } from './textarea';

export interface StepUpReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Submit button label. Defaults to "Proceed". */
  submitLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Whether the underlying mutation is in flight — disables the submit
   * button + shows a pending state.
   */
  submitting?: boolean;
  /**
   * Optional placeholder text for the reason textarea.
   * Default: "Ticket / change request / incident reference".
   */
  placeholder?: string;
  /**
   * Called with the validated reason when the operator clicks Proceed.
   * The caller is responsible for closing the dialog (typically via
   * the mutation's `onSuccess` callback).
   */
  onConfirm: (reason: string) => void;
}

/** Same bounds as the backend's require-fresh-totp.ts gate (D2 §L3). */
const REASON_MIN = 3;
const REASON_MAX = 500;

export function StepUpReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel = 'Proceed',
  cancelLabel = 'Cancel',
  submitting = false,
  placeholder = 'Ticket / change request / incident reference (e.g. CHG-2026-04-12)',
  onConfirm,
}: StepUpReasonDialogProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const len = trimmed.length;
  const valid = len >= REASON_MIN && len <= REASON_MAX;

  // Helper so the parent doesn't have to re-trim before forwarding the
  // value into the mutation's `stepUpReason` field.
  const handleConfirm = () => {
    if (!valid) return;
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="step-up-reason" className="text-xs font-semibold">
            Audit reason <span className="text-muted-foreground">(required)</span>
          </Label>
          <Textarea
            id="step-up-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
            className="min-h-[88px] text-sm"
            maxLength={REASON_MAX + 50}
            data-testid="step-up-reason-input"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>
              {len < REASON_MIN
                ? `At least ${REASON_MIN} characters required.`
                : len > REASON_MAX
                  ? `At most ${REASON_MAX} characters.`
                  : 'Recorded in the platform_audit_logs row.'}
            </span>
            <span className="tabular-nums">
              {len} / {REASON_MAX}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {cancelLabel}
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!valid || submitting}
            data-testid="step-up-reason-submit"
          >
            {submitting ? 'Submitting…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
